const MapModule = {
  map: null,
  infowindow: null,
  markers: [],
  markerData: [],
  /** Map station id → AdvancedMarkerElement (indeks markers[] tidak sama dengan markerData[]) */
  markerByStationId: {},
  layers: {},
  _isReady: false,
  _readyResolve: null,
  _readyPromise: null,

  _normalizeStationId(id) {
    return id == null ? "" : String(id);
  },

  _hasValidCoords(lat, lng) {
    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180
    );
  },

  /**
   * Initialize map
   */
  isReady() {
    return this._isReady && !!this.map && !!window.google?.maps;
  },

  async init(config) {
    console.log("🗺️ MapModule.init called", config);

    this._readyPromise = new Promise((resolve) => {
      this._readyResolve = resolve;
    });

    try {
      await DashboardHelpers.waitForGoogleMaps();

      const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
      const lat = parseFloat(config.latitude) || -1.269160;
      const lng = parseFloat(config.longitude) || 116.825264;
      const zoom = parseInt(config.zoom) || 5;
      const deferMarkers = !!config.deferMarkers;

      this.map = new google.maps.Map(document.getElementById("map"), {
        center: { lat, lng },
        zoom,
        mapId: DashboardConfig.map.mapId,
        mapTypeId: "hybrid",
        streetViewControl: false,
      });

      this.infowindow = new google.maps.InfoWindow({
        maxWidth: 400,
        minWidth: 280,
      });

      this.map.addListener("zoom_changed", () => {
        const currentData = this.markerData;
        this.clearMarkers();
        this.addMarkers(currentData);
      });

      await this.loadLegend();

      if (!deferMarkers) {
        await this.fetchMarkers();
        await this.loadRiverLayers();
        await this.loadWatershedLayers();
      }

      this._isReady = true;
      console.log("✅ MapModule is fully ready");
    } catch (err) {
      this._isReady = false;
      console.error("Map init failed:", err);
      DashboardHelpers.showMapLoadError(
        "Peta tidak dapat dimuat. Data monitoring dan CCTV tetap tersedia.",
      );
      throw err;
    } finally {
      if (this._readyResolve) {
        this._readyResolve();
      }
    }
  },

  /**
   * Returns a promise that resolves when the map is ready
   */
  ready() {
    if (this._isReady) return Promise.resolve();
    if (!this._readyPromise) {
      // If init hasn't been called yet, create the promise
      this._readyPromise = new Promise((resolve) => {
        this._readyResolve = resolve;
      });
    }
    return this._readyPromise;
  },

  /**
   * Trigger map resize (needed when container was 0-height or viewport changed)
   */
  triggerResize() {
    if (!this.map) return;
    const center = this.map.getCenter();
    google.maps.event.trigger(this.map, "resize");
    if (center) {
      this.map.setCenter(center);
    }
  },

  /**
   * Fetch markers from DashboardAPI
   */
  async fetchMarkers() {
    if (!this.isReady()) return [];

    try {
      const data = await DashboardAPI.getMarkers();
      this.clearMarkers();
      this.markerData = data || [];
      this.markerByStationId = {};
      await this.addMarkers(this.markerData);
      return this.markerData;
    } catch (error) {
      console.error("Error fetching markers:", error);
      throw error;
    }
  },

  /**
   * Add markers to map
   */
  //async addMarkers(data) {
  //  const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

  //  data.forEach((station) => {
  //    if (!station.latitude || !station.longitude) return;

  //    const customMarker = this.createMarkerElement(station);

  //    const marker = new AdvancedMarkerElement({
  //      map: this.map,
  //      position: {
  //        lat: parseFloat(station.latitude),
  //        lng: parseFloat(station.longitude),
  //      },
  //      content: customMarker,
  //    });

  //    this.markers.push(marker);

  //    // Add click listener
  //    marker.addListener("click", () => {
  //      this.showInfoWindow(station, marker);
  //    });
  //  });
  //},

  /**
   * Add markers to map - WITH AUTOMATIC OFFSET
   */
  /**
   * Add markers to map - Hanya geser jika koordinat duplikat
   */
  async addMarkers(data) {
    if (!this.isReady()) return;

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const coordMap = new Map();

    // Ambil zoom level saat ini
    const currentZoom = this.map.getZoom();
    const dynamicOffset = this.getOffsetByZoom(currentZoom);

    data.forEach((station) => {
      if (
        typeof StationMarkerConfig !== "undefined" &&
        !StationMarkerConfig.shouldShowMarkerFromData(station)
      ) {
        return;
      }

      if (!station.latitude || !station.longitude) return;

      const rawLat = parseFloat(station.latitude);
      const rawLng = parseFloat(station.longitude);
      const coordKey = `${rawLat.toFixed(6)},${rawLng.toFixed(6)}`;

      let occurrence = coordMap.get(coordKey) || 0;

      let finalLat = rawLat;
      let finalLng = rawLng;

      if (occurrence > 0) {
        // Gunakan dynamicOffset yang sudah dihitung berdasarkan zoom
        // Kita gunakan sedikit variasi arah (diagonal) agar tidak menumpuk garis lurus
        finalLat += occurrence * dynamicOffset * 0.8;
        finalLng += occurrence * dynamicOffset * 0.8;
      }

      coordMap.set(coordKey, occurrence + 1);

      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: finalLat, lng: finalLng },
        content: this.createMarkerElement(station),
      });

      this.markers.push(marker);
      this.markerByStationId[this._normalizeStationId(station.id)] = marker;
      marker.addListener("click", () => this.showInfoWindow(station, marker));
    });
  },

  getOffsetByZoom(zoom) {
    // Formula dasar: Semakin kecil zoom, semakin besar offset koordinat yang dibutuhkan
    // 360 derajat / (256 * 2^zoom) adalah ukuran derajat per pixel
    const shiftInPixel = 35; // Jarak antar marker dalam pixel yang diinginkan
    return shiftInPixel * (360 / (Math.pow(2, zoom) * 256));
  },

  /**
   * Create marker element test
   */
  createMarkerElement(station) {
    const customMarker = document.createElement("div");
    customMarker.className =
      typeof StationMarkerConfig !== "undefined"
        ? StationMarkerConfig.getMarkerClassFromData(station)
        : this.getLegacyMarkerClass(station);
    return customMarker;
  },

  getLegacyMarkerClass(station) {
    const customMarkerClass = document.createElement("div");

    switch (station.type) {
      case DashboardConstants.STATION_TYPES.AWLR:
      case DashboardConstants.STATION_TYPES.TMAS:
        customMarkerClass.className = this.getAwlrMarkerClass(
          parseInt(station.status, 10),
          DashboardConstants.STATION_TYPES.AWLR,
        );
        break;
      case DashboardConstants.STATION_TYPES.TMAT:
      case DashboardConstants.STATION_TYPES.AGWLR:
        customMarkerClass.className = this.getAwlrMarkerClass(
          parseInt(station.status, 10),
          DashboardConstants.STATION_TYPES.AGWLR,
        );
        break;
      case DashboardConstants.STATION_TYPES.OW:
        customMarkerClass.className = "ow-marker";
        break;
      case DashboardConstants.STATION_TYPES.ARR:
        customMarkerClass.className = "arr-marker";
        break;
      case DashboardConstants.STATION_TYPES.AWLR_ARR:
        customMarkerClass.className = `awlr-arr-marker ${this.getWarningClass(
          parseInt(station.status, 10),
        )}`;
        break;
      case DashboardConstants.STATION_TYPES.AWS:
        customMarkerClass.className = "aws-marker";
        break;
      case DashboardConstants.STATION_TYPES.FM:
        customMarkerClass.className = "fm-marker";
        break;
      case DashboardConstants.STATION_TYPES.WQ:
        customMarkerClass.className = "wq-marker";
        break;
      case DashboardConstants.STATION_TYPES.VNOTCH:
        customMarkerClass.className = "vnotch-marker";
        break;
      case DashboardConstants.STATION_TYPES.OSP:
        customMarkerClass.className = "osp-marker";
        break;
      case DashboardConstants.STATION_TYPES.AVWR:
        customMarkerClass.className = "avwr-marker";
        break;
      default:
        customMarkerClass.className = "default-marker";
    }

    return customMarkerClass.className;
  },

  /**
   * Get AWLR marker class based on status
   */
  getAwlrMarkerClass(status, type) {
    const baseClass =
      type === DashboardConstants.STATION_TYPES.AGWLR
        ? "agwlr-marker"
        : "awlr-marker";
    const statusMap = {
      0: "",
      1: "alert-siaga1",
      2: "alert-siaga2",
      3: "alert-siaga3",
    };
    return `${baseClass} ${statusMap[status] || ""}`.trim();
  },

  /**
   * Get warning class
   */
  getWarningClass(status) {
    //alert(status);
    const statusMap = {
      0: "",
      1: "alert-siaga1",
      2: "alert-siaga2",
      3: "alert-siaga3",
    };
    return statusMap[status] || "";
  },

  /**
   * Show info window - ✨ UPDATED
   */
  showInfoWindow(station, marker) {
    if (this.infowindow) {
      this.infowindow.close();
    }

    // Generate popup content menggunakan PopupModule
    const content = PopupModule.generate(station);

    this.infowindow.setContent(content);
    this.infowindow.open({
      anchor: marker,
      map: this.map,
    });

    // ✨ Tambahkan event listener untuk tombol "Lihat Detail"
    // Tunggu InfoWindow selesai di-render
    google.maps.event.addListenerOnce(this.infowindow, "domready", () => {
      // Cari semua link detail di dalam InfoWindow
      const detailLinks = document.querySelectorAll(
        '.btn-primary[href*="/Station/Detail/"]',
      );

      detailLinks.forEach((link) => {
        link.addEventListener("click", (e) => {
          const stationType = String(station.type || "").toUpperCase();
          // AVWR: no dashboard chart — open Station Detail in a new tab.
          if (stationType === "AVWR") {
            e.preventDefault();
            e.stopPropagation();
            if (station.slug) {
              window.open(`/Station/Detail/${station.slug}`, "_blank");
            }
            return;
          }

          // Tipe lain: pilih card di leftbar (grafik dashboard), jangan navigasi.
          e.preventDefault();
          this.triggerStationDetail(station);
        });
      });
    });
  },

  /**
   * ✨ FUNGSI BARU: Trigger detail station dari map marker
   */
  triggerStationDetail(station) {
    const stationType = String(station.type || "").toUpperCase();
    if (stationType === "AVWR") {
      if (station.slug) {
        window.open(`/Station/Detail/${station.slug}`, "_blank");
      }
      return;
    }

    const resolvedType =
      stationType === DashboardConstants.STATION_TYPES.TMAT
        ? DashboardConstants.STATION_TYPES.AGWLR
        : stationType;

    const tabMapping = {
      AWLR: "tab-awlr",
      TMAS: "tab-awlr",
      TMAT: "tab-tmat",
      AGWLR: "tab-tmat",
      OW: "tab-ow",
      ARR: "tab-arr",
      AWLR_ARR: "tab-awlr-arr",
      AWS: "tab-aws",
      FM: "tab-fm",
      WQ: "tab-wq",
      AVWR: "tab-avwr",
    };

    const tabId = tabMapping[resolvedType] || "tab-awlr";

    // Aktifkan tab yang sesuai
    const tabTrigger = document.querySelector(`a[href="#${tabId}"]`);
    if (tabTrigger) {
      // Gunakan Bootstrap 5 Tab API
      const tab = new bootstrap.Tab(tabTrigger);
      tab.show();
    }

    // Tunggu tab selesai ditampilkan, lalu trigger card
    setTimeout(() => {
      // Cari card element berdasarkan station ID dan type
      let cardSelector = "";

      switch (resolvedType) {
        case "AGWLR":
          cardSelector = `#card-station-tmat-${station.id}`;
          break;
        case "AWLR":
        case "TMAS":
          cardSelector = `#card-station-awlr-${station.id}`;
          break;
        case "ARR":
          cardSelector = `#card-station-arr-${station.id}`;
          break;
        case "AWLR_ARR":
          cardSelector = `#card-station-awlr-arr-${station.id}`;
          break;
        case "AWS":
          cardSelector = `#card-station-aws-${station.id}`;
          break;
        case "FM":
          cardSelector = `#card-station-fm-${station.id}`;
          break;
        case "WQ":
          cardSelector = `#card-station-wq-${station.id}`;
          break;
        case "AVWR":
          cardSelector = `#card-station-avwr-${station.id}`;
          break;
      }

      const cardElement = document.querySelector(cardSelector);

      if (cardElement) {
        // Scroll ke card
        cardElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Tunggu scroll selesai, lalu trigger click
        setTimeout(() => {
          // Trigger click event pada card (akan memanggil StationModule.showDetail)
          cardElement.click();

          // Close InfoWindow setelah card di-click
          this.infowindow.close();
        }, 500);
      } else {
        console.warn(`⚠️ Card not found: ${cardSelector}`);

        // Fallback: panggil StationModule.showDetail secara langsung
        if (typeof StationModule !== "undefined") {
          // Buat dummy element dengan data attributes
          const dummyElement = document.createElement("div");
          dummyElement.setAttribute("data-ts-id", station.id);
          dummyElement.setAttribute("data-name", station.name);
          dummyElement.setAttribute("data-device", station.deviceId);
          const detailType =
            resolvedType === DashboardConstants.STATION_TYPES.TMAT
              ? DashboardConstants.STATION_TYPES.AGWLR
              : resolvedType;
          dummyElement.setAttribute("data-type", detailType);

          if (
            detailType === "AWLR" ||
            detailType === "AGWLR" ||
            detailType === "TMAS"
          ) {
            dummyElement.setAttribute("data-station-type", detailType);
            dummyElement.setAttribute("data-siaga1", station.siaga1 || "");
            dummyElement.setAttribute("data-siaga2", station.siaga2 || "");
            dummyElement.setAttribute("data-siaga3", station.siaga3 || "");
          }

          StationModule.showDetail(dummyElement, new Event("click"));

          // Close InfoWindow
          this.infowindow.close();
        } else {
          console.error("❌ StationModule not available");
        }
      }
    }, 300);
  },

  /**
   * @param {string|number} stationId
   * @param {object} [fallbackStation] — data dari kartu kiri jika belum ada di markerData
   * @returns {boolean} true jika popup berhasil dibuka
   */
  showMarkerPopup(stationId, fallbackStation) {
    if (
      typeof DashboardHelpers !== 'undefined' &&
      typeof DashboardHelpers.scrollToDashboardMap === 'function'
    ) {
      DashboardHelpers.scrollToDashboardMap();
    } else {
      const mapContainer = document.getElementById('map');
      if (mapContainer) {
        mapContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }

    const scrollDelay =
      typeof DashboardHelpers !== 'undefined' &&
      typeof DashboardHelpers.isMobileDashboard === 'function' &&
      DashboardHelpers.isMobileDashboard()
        ? 550
        : 300;

    const id = this._normalizeStationId(stationId);
    let station = this.markerData.find(
      (s) => this._normalizeStationId(s.id) === id,
    );

    if (!station && fallbackStation) {
      station = {
        ...fallbackStation,
        id: fallbackStation.id ?? stationId,
        type: (fallbackStation.type || "").toUpperCase(),
      };
    }

    if (!station) {
      console.warn("⚠️ Station not found in markerData:", stationId);
      return false;
    }

    const lat = parseFloat(station.latitude);
    const lng = parseFloat(station.longitude);
    if (!this._hasValidCoords(lat, lng)) {
      return false;
    }

    const marker = this.markerByStationId[id];

    setTimeout(() => {
      this._openInfoWindowAtStation(station, marker || null);
    }, scrollDelay);

    return true;
  },
  /**
   * ✨ FUNGSI BARU: Focus map pada station
   */
  focusOnStation(stationId) {
    const id = this._normalizeStationId(stationId);
    const station = this.markerData.find(
      (s) => this._normalizeStationId(s.id) === id,
    );

    if (!station) {
      console.warn("⚠️ Station not found:", stationId);
      return;
    }

    // Center map dan zoom
    this.map.setCenter({
      lat: parseFloat(station.latitude),
      lng: parseFloat(station.longitude),
    });

    this.map.setZoom(14);
  },

  /**
   * Toggle layer visibility
   */
  toggleLayer(layerName, show) {
    if (this.layers[layerName]) {
      this.layers[layerName].setMap(show ? this.map : null);
    }
  },

  /**
   * Clear all markers
   */
  clearMarkers() {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
    this.markerByStationId = {};
  },

  /**
   * Buka InfoWindow di koordinat stasiun (tanpa anchor marker).
   */
  _openInfoWindowAtStation(station, marker) {
    const lat = parseFloat(station.latitude);
    const lng = parseFloat(station.longitude);

    if (!this._hasValidCoords(lat, lng)) {
      return false;
    }

    if (this.infowindow) {
      this.infowindow.close();
    }

    const normalizedStation = {
      ...station,
      type: (station.type || "").toUpperCase(),
    };
    const content = PopupModule.generate(normalizedStation);
    this.infowindow.setContent(content);

    this.map.panTo({ lat, lng });
    const currentZoom = this.map.getZoom();
    const targetZoom = currentZoom < 13 ? 13 : currentZoom;
    this.map.setZoom(targetZoom);

    const openOptions = marker
      ? { anchor: marker, map: this.map }
      : { map: this.map, position: { lat, lng } };
    this.infowindow.open(openOptions);

    if (marker && marker.content) {
      marker.content.classList.add("marker-bounce");
      setTimeout(() => {
        marker.content.classList.remove("marker-bounce");
      }, 2000);
    }

    return true;
  },

  /**
   * Filter markers by type
   */
  filterMarkers(types) {
    this.clearMarkers();
    const filteredData = this.markerData.filter((station) => {
      if (typeof StationMarkerConfig === "undefined") {
        return types.includes(station.type);
      }

      const markerType = StationMarkerConfig.resolveDataType(station.type);
      return types.includes(markerType) || types.includes(station.type);
    });
    this.addMarkers(filteredData);
  },

  /**
   * Load legend
   */
  async loadLegend() {
    try {
      const response = await DashboardAPI.get("/station/type/total");

      let legendHtml =
        '<div class="py-1 px-2" style="background-color: white;box-shadow: 0 0 15px rgba(0, 0, 0, 0.3); border-radius: 1px; margin-bottom:10px">';
      legendHtml += '<h6 class="fw-bold py-1 px-0 m-1">Tipe Pos</h6>';

      response.forEach((item) => {
        const legendEntry =
          typeof StationMarkerConfig !== "undefined"
            ? StationMarkerConfig.buildLegendEntry(item.type)
            : DashboardConstants.STATION_CONFIG[item.type]
              ? {
                  type: item.type,
                  name: DashboardConstants.STATION_CONFIG[item.type].name,
                  classMarker:
                    DashboardConstants.STATION_CONFIG[item.type].classMarker,
                  filterValue:
                    typeof StationMarkerConfig !== "undefined"
                      ? StationMarkerConfig.getFilterValue(item.type)
                      : item.type === "TMAT" || item.type === "AGWLR"
                        ? "AGWLR"
                        : item.type,
                }
              : null;

        if (legendEntry) {
          legendHtml += `
                        <div class="d-flex align-items-center mb-1 justify-content-between">
                            <div class="${legendEntry.classMarker}" style="width: 18px;height: 18px;box-shadow:none;"></div>
                            <div class="me-1" style="font-weight: bold;">${legendEntry.name}</div>
                            <input name="tipe-sensor" value="${legendEntry.filterValue}" type="checkbox" checked onchange="MapModule.handleMarkerFilter(this)" />
                        </div>
                    `;
        }
      });

      legendHtml += StationMarkerConfig.buildLegendFootnoteHtml();
      legendHtml += "</div>";

      const legendDiv = document.createElement("div");
      legendDiv.innerHTML = legendHtml;
      this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(
        legendDiv,
      );
    } catch (error) {
      console.error("Error loading legend:", error);
    }
  },

  /**
   * Handle marker filter checkbox
   */
  handleMarkerFilter(checkbox) {
    const checkedTypes = Array.from(
      document.querySelectorAll('input[name="tipe-sensor"]:checked'),
    ).map((cb) => cb.value);

    this.filterMarkers(checkedTypes);
  },

  /**
   * Load river layers
   */
  async loadRiverLayers() {
    try {
      const rivers = await DashboardAPI.get("/Home/River/Files");

      rivers.forEach((river) => {
        this.loadRiverFile(river.fileName);
      });
    } catch (error) {
      console.error("Error loading river layers:", error);
    }
  },

  /**
   * Load single river file
   */
  loadRiverFile(fileName) {
    const layer = new google.maps.Data();
    const layerName = fileName.split(".")[0];
    this.layers[layerName] = layer;

    $.getJSON(
      `/data/rivers/${DashboardServerConfig.organizationCode}/${fileName}`,
      (data) => {
        for (let key in data.objects) {
          if (data.objects.hasOwnProperty(key)) {
            const geoJsonObject = topojson.feature(data, data.objects[key]);

            geoJsonObject.features.forEach((feature) => {
              if (feature.properties.NAMA_SUNGA || feature.properties.SUNGAI) {
                const riverName =
                  feature.properties.NAMA_SUNGA || feature.properties.SUNGAI;
                const addedFeature = layer.addGeoJson(feature);

                addedFeature.forEach((f) => {
                  f.setProperty("riverName", riverName);
                });
              }
            });
          }
        }

        layer.setStyle({
          fillColor: "#4068D8",
          fillOpacity: 1,
          strokeColor: "#4068D8",
          strokeWeight: 2,
          strokeOpacity: 1,
        });

        layer.setMap(this.map);

        // Add hover effects
        this.addRiverLayerEvents(layer);

        // Add toggle control
        document
          .getElementById("toggle-river")
          ?.addEventListener("change", (e) => {
            this.toggleLayer(layerName, e.target.checked);
          });
      },
    );
  },

  /**
   * Add river layer eventss
   */
  addRiverLayerEvents(layer) {
    layer.addListener("mouseover", (event) => {
      const name = event.feature.getProperty("riverName");
      const label = document.getElementById("das-label");

      if (name) {
        label.textContent = "SUNGAI: " + name;
        label.style.display = "block";

        layer.overrideStyle(event.feature, {
          strokeWeight: 2,
          fillOpacity: 0.8,
        });
      }
    });

    layer.addListener("mouseout", (event) => {
      document.getElementById("das-label").style.display = "none";
      layer.revertStyle(event.feature);
    });
  },

  /**
   * Load watershed layers
   */
  async loadWatershedLayers() {
    try {
      const riverAreas = await DashboardAPI.get("/RiverArea/GetAllRiverArea");

      if (riverAreas.code === 200) {
        riverAreas.response.forEach((riverArea, index) => {
          if (riverArea.shpFile != null && riverArea.shpFile !== "") {
            this.loadWatershedFile(riverArea, index);
          }
        });
      }
    } catch (error) {
      console.error("Error loading watershed layers:", error);
    }
  },

  /**
   * Load single watershed file
   */
  loadWatershedFile(riverArea, colorOffset = 0) {
    const colors = [
      "#e6194b",
      "#3cb44b",
      "#ffe119",
      "#4363d8",
      "#f58231",
      "#911eb4",
      "#46f0f0",
      "#f032e6",
      "#bcf60c",
      "#fabebe",
      "#008080",
      "#e6beff",
      "#9a6324",
      "#fffac8",
      "#800000",
      "#aaffc3",
      "#808000",
      "#ffd8b1",
      "#000075",
    ];

    const layer = new google.maps.Data();
    const layerName = riverArea.shpFile.split(".")[0];
    this.layers[layerName] = layer;

    $.getJSON(
      `/data/watersheds/${riverArea.organizationCode}/${riverArea.shpFile}`,
      (data) => {
        let colorIndex = colorOffset;

        for (let key in data.objects) {
          if (data.objects.hasOwnProperty(key)) {
            const geoJsonObject = topojson.feature(data, data.objects[key]);

            geoJsonObject.features.forEach((das) => {
              const addedFeature = layer.addGeoJson(das);

              addedFeature.forEach((f) => {
                // Simpan properti warna dan nama DAS
                f.setProperty("colorIndex", colorIndex % colors.length);
                f.setProperty("areaName", das.properties.NAMA_DAS || "Unknown");
                f.setProperty("riverAreaName", riverArea.name || "");
              });

              colorIndex++;
            });
          }
        }

        // Terapkan style berdasarkan colorIndex
        layer.setStyle((feature) => {
          const index = feature.getProperty("colorIndex");
          return {
            fillColor: colors[index],
            fillOpacity: 0.2,
            strokeColor: "#fff",
            strokeWeight: 1,
            strokeOpacity: 0.5,
          };
        });

        // Set layer ke map
        layer.setMap(this.map);

        // Add hover effects
        this.addWatershedLayerEvents(layer);

        // Add toggle control
        document
          .getElementById("toggle-watershed")
          ?.addEventListener("change", (e) => {
            this.toggleLayer(layerName, e.target.checked);
          });
      },
    ).fail((error) => {
      console.error(
        `Error loading watershed file: ${riverArea.shpFile}`,
        error,
      );
    });
  },

  /**
   * Add watershed layer events
   */
  addWatershedLayerEvents(layer) {
    layer.addListener("mouseover", (event) => {
      const name = event.feature.getProperty("areaName");
      const label = document.getElementById("das-label");

      if (name) {
        label.textContent = "DAS: " + name;
        label.style.display = "block";

        // Ubah style saat hover
        layer.overrideStyle(event.feature, {
          strokeWeight: 2,
          fillOpacity: 0.5,
        });
      }
    });

    layer.addListener("mouseout", (event) => {
      const label = document.getElementById("das-label");
      label.style.display = "none";

      // Kembalikan style ke semula
      layer.revertStyle(event.feature);
    });
  },
};

// Expose to global for onclick events
window.changeMarker = (checkbox) => {
  MapModule.handleMarkerFilter(checkbox);
};
